'use client';

import { useEffect, useMemo, useState } from 'react';
import type { LobbyGridPage } from '@/hooks/use-lobby-grid-page';
import type { SeatData } from '@/components/grid';
import { getTaxTokenConfig } from '@/lib/drawer-utils';
import { TAKEOVER_TOKEN_ADDRESS } from '@/lib/constants/addresses';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { getBoardroomLiveGridId, resolveLobbyLiveGridId } from './boardroom-grid';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';
import {
  TUTORIAL_BOARD_ID,
  TUTORIAL_TAX_TOKEN_DECIMALS,
  TUTORIAL_VIEWER,
} from '@/features/tutorial/tutorial-board';

interface RawSeat {
  seatIndex: number;
  holder: string | null;
  holderName?: string | null;
  holderAvatar?: string | null;
  holderLevel?: number;
  selfAssessedPrice: string;
  balance: string;
  runwaySeconds: number;
  claimedAt?: number;
  lastUpdateTime?: number;
  isForfeited?: boolean;
  isReserved?: boolean;
  isOwn?: boolean;
  rank?: number;
}

/** Demo tiles we mark as "owned" in the snapshot so the lobby can show
 *  a populated "My Seats" pill strip + coloured 'own' tier on 4 different
 *  tiles. Indices picked to span urgency levels:
 *   - 67  (~13h runway) — close to expiry, shows the warning pill treatment
 *   - 12  (~32 days)    — existing demo owner
 *   - 60  (~28 days)    — moderate runway
 *   - 5   (~25 days)    — moderate runway, different price band
 *
 *  Empty in production: the snapshot fallback never runs in production
 *  builds (see `useSnapshotSeats` below) and even if it did, marking
 *  seats as `isOwn` for an unauthenticated viewer would be misleading.
 */
const DEMO_OWNED_SEAT_INDICES: Set<number> =
  process.env.NEXT_PUBLIC_APP_MODE === 'production'
    ? new Set<number>()
    : new Set<number>([67, 12, 60, 5]);

const SNAPSHOT_FALLBACK_ENABLED = process.env.NEXT_PUBLIC_APP_MODE !== 'production';

function mapSnapshotSeats(raw: RawSeat[]): SeatData[] {
  return raw.map((s) => ({
    seatIndex: s.seatIndex,
    holder: s.holder,
    holderAvatar: s.holderAvatar ?? null,
    holderName: s.holderName ?? null,
    holderLevel: s.holderLevel,
    selfAssessedPrice: BigInt(s.selfAssessedPrice),
    balance: BigInt(s.balance),
    runwaySeconds: s.runwaySeconds,
    claimedAt: s.claimedAt,
    lastUpdateTime: s.lastUpdateTime,
    isForfeited: !!s.isForfeited,
    isReserved: !!s.isReserved,
    isOwn: DEMO_OWNED_SEAT_INDICES.has(s.seatIndex) || !!s.isOwn,
    rank: s.rank,
  }));
}

/**
 * Lazy-loaded boardroom snapshot. Previously a top-level `import` of
 * `boardroom-snapshot.json` (35KB) baked the fixture into the main lobby
 * bundle even for users whose active board resolves to a live
 * composite grid id — i.e. everyone except viewers of Boardroom or
 * fallback dev mocks.
 *
 * The cache below makes the first caller pay a one-off fetch against
 * `/data/boardroom-snapshot.json` (served statically from `public/`);
 * subsequent consumers re-use the parsed `SeatData[]`. Module-level
 * storage is fine because the data is immutable and the 100 entries
 * are cheap to keep around for the session.
 */
let snapshotSeatsCache: SeatData[] | null = null;
let snapshotSeatsPromise: Promise<SeatData[]> | null = null;

function loadSnapshotSeats(): Promise<SeatData[]> {
  if (snapshotSeatsCache) return Promise.resolve(snapshotSeatsCache);
  if (snapshotSeatsPromise) return snapshotSeatsPromise;
  snapshotSeatsPromise = fetch('/data/boardroom-snapshot.json', { cache: 'force-cache' })
    .then((r) => {
      if (!r.ok) throw new Error(`boardroom-snapshot.json HTTP ${r.status}`);
      return r.json() as Promise<RawSeat[]>;
    })
    .then((raw) => {
      snapshotSeatsCache = mapSnapshotSeats(raw);
      return snapshotSeatsCache;
    })
    .catch((err) => {
      console.warn('[useLobbySeats] snapshot fetch failed; using empty fallback', err);
      snapshotSeatsCache = [];
      return snapshotSeatsCache;
    })
    .finally(() => {
      snapshotSeatsPromise = null;
    });
  return snapshotSeatsPromise;
}

/**
 * Hook that returns the parsed snapshot seats once the JSON finishes
 * loading. Returns `null` on the initial render to signal "not ready
 * yet" — callers should fall back to empty seats during that window
 * rather than synthesising demo data (keeps the first paint quiet for
 * boards that will end up resolving to live mode anyway).
 *
 * The network request is only initiated when `enabled=true`, so the
 * common path (live board ids) never touches the snapshot file at all.
 */
function useSnapshotSeats(enabled: boolean): SeatData[] | null {
  const [seats, setSeats] = useState<SeatData[] | null>(() => snapshotSeatsCache);

  useEffect(() => {
    if (!enabled) return;
    if (snapshotSeatsCache) {
      setSeats(snapshotSeatsCache);
      return;
    }
    let cancelled = false;
    loadSnapshotSeats().then((data) => {
      if (!cancelled) setSeats(data);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return seats;
}

function emptySeat(index: number): SeatData {
  return {
    seatIndex: index,
    holder: null,
    selfAssessedPrice: 0n,
    balance: 0n,
    runwaySeconds: 0,
    isForfeited: false,
    isOwn: false,
  };
}

/** Tiny deterministic 32-bit hash. Not cryptographic — just a stable PRNG
 *  seed generator so every synthetic seat looks the same across renders. */
function hash32(seed: number): number {
  let x = seed | 0;
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  return (x ^ (x >>> 16)) >>> 0;
}

/** Pseudo-holder names spanning a range of visual weights so the
 *  synthetic stress-test rows read as plausibly populated (rather than
 *  all blank or all "user123"). */
const SYNTH_HOLDER_NAMES = [
  'aurora', 'baron', 'corvid', 'delph', 'ember', 'fang', 'gryph',
  'hollow', 'ivor', 'jaden', 'kestrel', 'lark', 'mote', 'nyx',
  'onyx', 'prism', 'quill', 'rune', 'saber', 'tundra', 'umber',
  'vex', 'wisp', 'xen', 'yarrow', 'zephyr',
];

/**
 * Build a deterministic synthetic seat for indices beyond the 100-seat
 * snapshot — used when a stress-test board requests 256 seats but the
 * fixture only provides 100. The layout deliberately mirrors the
 * snapshot's density (~93% held, levels 1-5, prices in the same band,
 * occasional forfeits) so the larger grid doesn't read as a half-empty
 * wasteland.
 */
function syntheticSeat(index: number): SeatData {
  const r1 = hash32(index);
  const r2 = hash32(index ^ 0xa5a5a5a5);
  const r3 = hash32(index ^ 0x5a5a5a5a);

  // ~8% vacant to match the snapshot's feel (7/100 empty).
  if (r1 % 100 < 8) return emptySeat(index);

  // Price band: 250M – 950M raw (0.25 – 0.95 USD at 6dp). Keeps the
  // ranking UI meaningful without crowding the top-tier gold colour.
  const priceRaw = 250_000_000 + (r2 % 700_000_001);
  const selfAssessedPrice = BigInt(priceRaw);

  // Balance spans ~0.5× – 5× price so runway varies naturally.
  const balMult = 5 + (r3 % 46) / 10; // 0.5..5.0
  const balance = selfAssessedPrice * BigInt(Math.round(balMult * 10)) / 10n;

  // Runway ≈ balance / price * 86400 (1-day units, matches snapshot scale).
  const runwaySeconds = Number(balance / (selfAssessedPrice === 0n ? 1n : selfAssessedPrice)) * 86_400;

  const holderName = SYNTH_HOLDER_NAMES[r2 % SYNTH_HOLDER_NAMES.length];
  // Weighted level: 1/2 most common, 5 rare. Mirrors what the snapshot
  // shows — most seats are mid-level, a handful are prestige.
  const levelRoll = r3 % 100;
  const holderLevel = levelRoll < 45 ? 1
    : levelRoll < 80 ? 2
    : levelRoll < 94 ? 3
    : levelRoll < 99 ? 4
    : 5;

  // ~3% forfeited so the lock overlay actually appears in the demo.
  const isForfeited = (r1 ^ r3) % 100 < 3;

  // Fake deterministic hex address so the holder looks real.
  const hexIdx = index.toString(16).padStart(8, '0');
  const holder = `0x${'00'.repeat(16)}${hexIdx}${hexIdx}`.slice(0, 42);

  return {
    seatIndex: index,
    holder,
    holderName,
    holderAvatar: null,
    holderLevel,
    selfAssessedPrice,
    balance,
    runwaySeconds,
    isForfeited,
    isReserved: false,
    isOwn: false,
  };
}

export interface UseLobbySeats {
  seats: SeatData[];
  /**
   * True when this board resolves to a real onchain grid id (HV boardroom
   * composite or another `0x…-n` id). Used to suppress lobby demo effects
   * (forfeit/waterfall seeding) — not the same as "holders have loaded".
   */
  isLive: boolean;
  /** Decimals of the grid's tax token (live) or the snapshot (6 for USDC-style). */
  taxTokenDecimals: number;
  /** Tax token symbol when known — used for compact labels. */
  taxTokenSymbol?: string;
  /** Optional USD price of one tax token (0 if unknown). */
  taxTokenPriceUsd: number;
  creatorAddress?: string;
  creatorName?: string;
  creatorAvatar?: string | null;
  creatorLevel?: number;
}

/**
 * Lobby seat source. Two modes:
 *
 * 1. **Live mode** — when the caller passes a real v2 `gridId` (a value
 *    matching the `0x…:n` shape) OR the canonical `'boardroom'` id with
 *    `HV_TAKEOVER_UGM_ADDRESS` configured, we route through
 *    `useGridPage(gridId)` so the canvas renders real seats from Envio
 *    + the live SSE poll.
 * 2. **Snapshot mode** — when no live id can be resolved (dev-only
 *    mocks like Hypergrid, or any board not sourced from
 *    `/api/v1/grids`), we fall back to the bundled
 *    `boardroom-snapshot.json` so prices/avatars/levels stay stable
 *    across every environment (local dev, preview, CI screenshots).
 *
 * Defaults to 100 seats (matching the snapshot). Callers rendering a
 * larger board (e.g. a 16×16 stress test with 256 seats) can pass
 * `{ totalSeats: 256 }` to pad beyond the snapshot with deterministic
 * synthetic seats via `syntheticSeat`. The snapshot is USDC-style
 * (6 decimals, 1 token = $1) so every chip in the UI is already a clean
 * dollar value without extra conversion.
 */
const DEFAULT_TOTAL_SEATS = 100;

export interface UseLobbySeatsOptions {
  /** Total seat count to return. Defaults to 100 (snapshot size).
   *  Larger values pad with deterministic synthetic seats so the grid
   *  never reads as half-empty on stress-test boards. */
  totalSeats?: number;
  /** Active board id. Used to detect "live mode" (real v2 grid id ⇒
   *  consume live seat data) vs. snapshot mode (boardroom / dev mock). */
  gridId?: string;
  /** Live grid page result hoisted from `useLobbyGridPage(activeBoardId)`
   *  in the parent. The seat hook used to call `useGridPage` itself,
   *  which (combined with the adapter + rewards hooks doing the same)
   *  opened multiple SSE connections per active board and tripped
   *  React's "Maximum update depth" guard. The parent owns the single
   *  source of truth now and threads it down. */
  gridPage: LobbyGridPage;
}

export function useLobbySeats(
  options: UseLobbySeatsOptions,
): UseLobbySeats {
  const totalSeats = Math.max(1, Math.floor(options.totalSeats ?? DEFAULT_TOTAL_SEATS));
  const tutorial = useTutorialOptional();

  // The boardroom mock id (`'boardroom'`) is also "live" once the
  // HV env vars are wired — `resolveLobbyLiveGridId` returns the
  // composite id of HV grid 0 in that case. Anything that doesn't
  // resolve (dev mocks, no env) drops back to the snapshot.
  const liveId = resolveLobbyLiveGridId(options.gridId);

  const {
    seats,
    grid,
    gridLoading,
    creatorIdentity,
    taxTokenDecimals,
    takeoverPriceUsdIndexer,
  } = options.gridPage;

  /** True once this board maps to a live composite grid id (not snapshot-only mocks). */
  const isLiveSurface = !!liveId;

  // Only fetch the demo snapshot when we actually need it (snapshot
  // mode). Live boards return `null` here and fall through to the
  // empty-seat path, keeping the 35KB JSON out of their critical path.
  //
  // Production never reaches snapshot mode — every legitimate board
  // resolves to a live composite grid id, and showing fake holders /
  // fake addresses to real users would be misleading. Hard-disabling
  // the fetch here also keeps the 35KB fixture out of every prod
  // bundle's critical path even on cold starts.
  const snapshotSeats = useSnapshotSeats(!isLiveSurface && SNAPSHOT_FALLBACK_ENABLED);

  /** When the indexer omits `taxToken` (e.g. Boardroom), infer TAKEOVER so 18‑dp amounts are not shown as USDC. */
  const resolvedTaxTokenConfig = useMemo(() => {
    if (grid?.taxToken) {
      return getTaxTokenConfig(grid.taxToken, undefined, grid.taxTokenDecimals ?? taxTokenDecimals);
    }
    if (!TAKEOVER_TOKEN_ADDRESS) {
      return getTaxTokenConfig(undefined, undefined, taxTokenDecimals);
    }
    const boardroomLive = getBoardroomLiveGridId();
    const treatAsTakeover =
      (boardroomLive != null && liveId === boardroomLive) ||
      (grid != null && grid.taxTokenDecimals != null && grid.taxTokenDecimals > 6) ||
      taxTokenDecimals > 6;
    if (!treatAsTakeover) {
      return getTaxTokenConfig(undefined, undefined, taxTokenDecimals);
    }
    return getTaxTokenConfig(
      TAKEOVER_TOKEN_ADDRESS as `0x${string}`,
      undefined,
      grid?.taxTokenDecimals ?? taxTokenDecimals ?? 18,
    );
  }, [grid, liveId, taxTokenDecimals]);

  // Seat rows are expensive to rebuild — keep `takeoverPriceUsdIndexer` out
  // of this memo so TAKEOVER USD ticks don't re-allocate 100–256 `SeatData`s.
  const {
    seats: padded,
    hasRenderableLiveSeats,
    taxTokenDecimals: outDecimals,
    taxTokenSymbol: outSymbol,
    creatorAddress,
    creatorName,
    creatorAvatar,
    creatorLevel,
  } = useMemo(() => {
    const liveActive = !!liveId;
    // Accept grids whose only "occupied" rows are unclaimed whitelist
    // reservations — without `|| s.isReserved`, a freshly-deployed
    // governance-gated grid (every seat reserved, none claimed yet)
    // failed `seats.some(holder)`, dropped `source` to `[]`, and ran
    // every row through `emptySeat(i)`. That stripped `isReserved` /
    // `reservedFor` / `reservedForAvatar` / `claimDeadline` before
    // they reached the renderers, so `tileTier` returned `'vacant'`,
    // tiles painted as muted-gray dashed (looks like cooldown) and
    // the green "+ OPEN" chip painted (looks available). Treating
    // a reservation as a renderable signal restores the data flow
    // for both 2D and 3D.
    const hasRenderableLiveSeats =
      liveActive
      && !gridLoading
      && Array.isArray(seats)
      && seats.some((s) => !!s.holder || s.isReserved);

    // Live load: show an overlay skeleton in the lobby (see `LobbyClient`)
    // instead of flashing snapshot data or an all-vacant “claimable” grid.
    // When the deferred snapshot hasn't resolved yet (`snapshotSeats ==
    // null`), we return an empty array — the grid renders all-vacant
    // seats for a frame or two until the fetch completes, then
    // re-renders via the state update from `useSnapshotSeats`.
    const source = hasRenderableLiveSeats
      ? seats
      : liveActive && gridLoading
        ? []
        : liveActive
          ? []
          : snapshotSeats ?? [];
    const padded: SeatData[] = new Array(totalSeats);
    for (let i = 0; i < totalSeats; i++) {
      const fromSource = source[i];
      if (fromSource) {
        padded[i] = fromSource;
      } else if (
        liveActive ||
        i < DEFAULT_TOTAL_SEATS ||
        !SNAPSHOT_FALLBACK_ENABLED
      ) {
        // In production, every "missing" seat falls back to empty so
        // we never paint deterministic-but-fake holders/prices on
        // boards larger than the snapshot fixture.
        padded[i] = emptySeat(i);
      } else {
        padded[i] = syntheticSeat(i);
      }
    }

    return {
      seats: padded,
      hasRenderableLiveSeats,
      taxTokenDecimals: hasRenderableLiveSeats ? (resolvedTaxTokenConfig?.decimals ?? taxTokenDecimals ?? 18) : 6,
      taxTokenSymbol: hasRenderableLiveSeats ? (resolvedTaxTokenConfig?.symbol ?? 'USDC') : 'USDC',
      creatorAddress: creatorIdentity?.address,
      creatorName: creatorIdentity?.displayName,
      creatorAvatar: creatorIdentity?.avatarUrl,
      creatorLevel: creatorIdentity?.level ?? 0,
    };
  }, [
    liveId,
    gridLoading,
    seats,
    creatorIdentity,
    taxTokenDecimals,
    totalSeats,
    resolvedTaxTokenConfig,
    snapshotSeats,
  ]);

  // Live TAKEOVER/USD price (polled RPC). Same fallback chain `main`'s
  // `MySeatsCard` uses on `boardroom/page.tsx` and the manage drawer at
  // `LobbyTileDrawers.tsx#L93-L94`: prefer the live oracle, fall back to
  // the indexer page payload, then 0. Without this, OwnedTilesBar can
  // single-source on `takeoverPriceUsdIndexer` and render zero-priced
  // pills any time that field is briefly missing/stale on the SSE-fed
  // grid page (the indexer itself stores 18dp now, so the math is fine
  // when the value is present — it's the absence that breaks the panel).
  const { priceUsd: liveTakeoverPriceUsd } = useTakeoverPrice();

  const taxTokenPriceUsd = useMemo(() => {
    if (!hasRenderableLiveSeats) return 1;
    if (!resolvedTaxTokenConfig?.isTakeover) return 1;
    return liveTakeoverPriceUsd || takeoverPriceUsdIndexer || 0;
  }, [
    hasRenderableLiveSeats,
    resolvedTaxTokenConfig,
    liveTakeoverPriceUsd,
    takeoverPriceUsdIndexer,
  ]);

  // Tutorial mode: override seats with the scripted tutorial board so the
  // canvas/panels render the same UI but against `TutorialContext` state.
  // We compute everything above so hook order stays constant whether or
  // not the consumer is mounted under `TutorialProvider`.
  if (tutorial && options.gridId === TUTORIAL_BOARD_ID) {
    return {
      seats: tutorial.state.seats,
      isLive: false,
      taxTokenDecimals: TUTORIAL_TAX_TOKEN_DECIMALS,
      taxTokenSymbol: 'USDC',
      taxTokenPriceUsd: 1,
      creatorAddress: TUTORIAL_VIEWER.address,
      creatorName: 'Tutorial host',
      creatorAvatar: TUTORIAL_VIEWER.avatarUrl,
      creatorLevel: 6,
    };
  }

  return {
    seats: padded,
    isLive: isLiveSurface,
    taxTokenDecimals: outDecimals,
    taxTokenSymbol: outSymbol,
    taxTokenPriceUsd,
    creatorAddress,
    creatorName,
    creatorAvatar,
    creatorLevel,
  };
}
